import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  appendOrganizationData,
  orgPreferencesRes,
  projectReferenceRes,
  resetOrganizationPagination,
  resetTeamsFetched,
  setActiveIntegrations,
  setActiveIntegrationsLoading,
  setAsanaSettings,
  setAsanaSettingsGetError,
  setAsanaSettingsGetSuccess,
  setAsanaSettingsLoading,
  setAsanaSettingsSaveError,
  setAsanaSettingsSaveSuccess,
  setAzureSettings,
  setAzureSettingsGetError,
  setAzureSettingsGetSuccess,
  setAzureSettingsLoading,
  setAzureSettingsSaveError,
  setAzureSettingsSaveSuccess,
  setCreateOrganizationData,
  setGithubSettings,
  setGithubSettingsError,
  setGithubSettingsLoading,
  setInviteUsersError,
  setInviteUsersLoading,
  setInviteUsersSuccess,
  setJiraSettings,
  setJiraSettingsGetError,
  setJiraSettingsGetSuccess,
  setJiraSettingsLoading,
  setJiraSettingsSaveError,
  setJiraSettingsSaveSuccess,
  setMondaySettings,
  setMondaySettingsGetError,
  setMondaySettingsGetSuccess,
  setMondaySettingsLoading,
  setMondaySettingsSaveError,
  setMondaySettingsSaveSuccess,
  setOnboardSettingsLoaded,
  setOrganizationData,
  setOrganizationError,
  setOrganizationLoading,
  setOrganizationLoadingMore,
  setOrganizationOnboardSettings,
  setOrganizationSkillData,
  setOrganizationSkillError,
  setOrganizationSkillLoading,
  setOrganizationUpdateError,
  setOrganizationUpdateLoading,
  setOrganizationUpdateSuccess,
  setOrgDetails,
  setOrgPreferencesError,
  setOrgPreferencesLoading,
  setOrgScheduleData,
  setOrgScheduleError,
  setOrgScheduleLoading,
  setOrgWorkingDays,
  setOrgWorkingDaysLoading,
  setToleranceModes,
  setToleranceModesLoading,
  setToleranceModesError,
  setPendingInvites,
  setPendingInvitesError,
  setPendingInvitesLoading,
  setProjectReferenceLoading,
  setProjectReferenceSaveError,
  setPinnedProjectsData,
  setRecentProjectsError,
  setRecentProjectsLoading,
  setRevokeInviteError,
  setRevokeInviteLoading,
  setRevokeInviteSuccess,
  setshoworgScheduleEdit,
  setShowSkillEdit,
  setTeams,
  setTeamsError,
  setTeamsLoading,
  setWorkItemsFilters,
  updatePreferenceId,
  // Work Items actions for combined API
  setWorkItemsData,
  appendWorkItemsData,
  setWorkItemsLoading,
  setWorkItemsLoadingMore,
  clearWorkItemsData,
  setWorkItemsSearchTerm,
} from "../redux/slices/organization";
import axios from "../utils/axiosConfig";
import { CombinedItem } from "../pages/Projects/roadmapsAndProjects.types";
import { errorHandler } from "../utils/errorHandler";
import store from "../redux/store";
import { Preference } from "../pages/Settings/types";
import toast from "react-hot-toast";
import {
  ProjectNamesResponse,
  RoadmapNamesResponse,
} from "../types/interfaces";
import {
  mergeProjectNamesById,
  mergeRoadmapNamesById,
} from "../redux/slices/settings";

interface Organization {
  org_id?: string;
  name: string;
  user_id: string;
  created_at?: Date;
  modified_at?: Date;
  business_type?: string;
  invite_user?: any;
  message?: string;
}
interface UpdateOrganization {
  org_id?: string;
  current_role?: string;
  department?: string;
  team_size?: string;
  company_size?: string;
  seniority?: string;
  priorities?: any;
  message?: string;
}

// Add interface for Sprint data
export interface Sprint {
  id: number;
  sprint_id: string;
  name: string;
  path: string;
  start_date: string;
  end_date: string;
  status: string;
  project_id: string;
  project_name: string;
  team_id: string;
  team_name: string;
}

const useOrganizationService = () => {
  const {
    data,
    loading: orgLoading,
    orgUpdateLoading,
    activeIntegrationsLoading,
  } = useSelector((state: any) => state.organization);
  const dispatch = useDispatch();

  const showError = (error: any) => {
    // Special handling for Jira connection error
    if (
      error?.response?.status === 422 &&
      error?.response?.data?.detail?.includes("Failed to connect to Jira")
    ) {
      // Use toast directly for this specific error
      toast.error("Please check your Jira credentials and base URL");
      return;
    }

    // Use centralized error handler for all other errors
    errorHandler.showError(error);
  };

  const fetchOrganization = async (org_id: any) => {
    if (orgLoading) return;

    dispatch(setOrganizationLoading(true));
    try {
      const response: any = await axios.get<Organization[]>(
        `/organization/${org_id}`,
      );
      // dispatch(setOrganizationData(response.data));
      dispatch(setOrgDetails(response?.data || {}));
      if (
        response?.data?.onboard_settings &&
        response?.data?.onboard_settings !== null
      ) {
        dispatch(
          setOrganizationOnboardSettings(response.data.onboard_settings),
        );
      } else {
        dispatch(setOrganizationOnboardSettings({}));
      }
      dispatch(setOnboardSettingsLoaded(true));
    } catch (error) {
      showError(error);
      dispatch(setOrganizationError(error));
    } finally {
      dispatch(setOrganizationLoading(false));
    }
  };

  const fetchAllProjects = async (
    org_id: string,
    reset: boolean = true,
    pageNumber?: number,
    searchTerm: string = "",
    searchType: string = "project",
    sortBy: string = "created_at_desc",
    source: string = "",
  ) => {
    if (reset) {
      if (pageNumber) {
        // If a specific page is requested, don't reset pagination state
        dispatch(setOrganizationLoading(true));
      } else {
        dispatch(resetOrganizationPagination());
        dispatch(setOrganizationLoading(true));
      }
    } else {
      dispatch(setOrganizationLoadingMore(true));
    }

    try {
      const { perPage = 10 } = store.getState().organization;
      const currentPage = reset
        ? pageNumber || 1 // Use the specified page number or default to 1
        : store.getState().organization.page + 1;

      // Include search parameter in API request
      const sourceParam = source ? `&source=${encodeURIComponent(source)}` : "";
      const response = await axios.get<Organization[]>(
        `/organization/${org_id}/projects?page=${currentPage}&per_page=${perPage}&search=${encodeURIComponent(searchTerm)}&search_type=${searchType}&sort_by=${sortBy}${sourceParam}`,
      );

      if (reset) {
        dispatch(setOrganizationData(response.data));
      } else {
        dispatch(appendOrganizationData(response.data));
      }
      // Populate global ID->name cache for projects
      try {
        const projects: any[] = (response as any)?.data?.projects || [];
        if (Array.isArray(projects) && projects.length > 0) {
          const mapped = projects.reduce<Record<string, string>>((acc, p) => {
            const id = String(p?.project_id ?? p?.id ?? "");
            if (!id) return acc;
            const name = String(p?.project_name ?? p?.name ?? id);
            acc[id] = name;
            return acc;
          }, {});
          if (Object.keys(mapped).length > 0) {
            dispatch(mergeProjectNamesById(mapped));
          }
        }
      } catch (e) {
        /* ignore */
      }
    } catch (error) {
      showError(error);
      dispatch(setOrganizationError(error));
    } finally {
      if (reset) {
        dispatch(setOrganizationLoading(false));
      } else {
        dispatch(setOrganizationLoadingMore(false));
      }
    }
  };
  const fetchProjectsList = async ({
    org_id,
    reset = true,
    pageNumber,
    searchTerm = "",
    searchType = "project",
    sortBy = "created_at_desc",
    source = "",
    include_data = [],
    item_type = "",
  }: {
    org_id: string;
    reset?: boolean;
    pageNumber?: number;
    searchTerm?: string;
    searchType?: string;
    sortBy?: string;
    source?: string;
    include_data?: string[];
    item_type?: string;
  }) => {
    if (reset) {
      if (pageNumber) {
        // If a specific page is requested, don't reset pagination state
        dispatch(setOrganizationLoading(true));
      } else {
        dispatch(resetOrganizationPagination());
        dispatch(setOrganizationLoading(true));
      }
    } else {
      dispatch(setOrganizationLoadingMore(true));
    }

    try {
      const { perPage = 10 } = store.getState().organization;
      const currentPage = reset
        ? pageNumber || 1 // Use the specified page number or default to 1
        : store.getState().organization.page + 1;

      // Include search parameter in API request
      const sourceParam = source ? `&source=${encodeURIComponent(source)}` : "";
      const includeDataParam =
        include_data.length > 0
          ? `&include_data=${include_data.join(",")}`
          : "";
      const itemTypeParam = item_type ? `&item_type=${item_type}` : "";

      const response = await axios.get<Organization[]>(
        `/organization/${org_id}/projects?page=${currentPage}&per_page=${perPage}&search=${encodeURIComponent(searchTerm)}&search_type=${searchType}&sort_by=${sortBy}${sourceParam}${includeDataParam}${itemTypeParam}`,
      );

      if (reset) {
        dispatch(setOrganizationData(response.data));
      } else {
        dispatch(appendOrganizationData(response.data));
      }
      // Populate global ID->name cache for projects
      try {
        const projects: any[] = (response as any)?.data?.projects || [];
        if (Array.isArray(projects) && projects.length > 0) {
          const mapped = projects.reduce<Record<string, string>>((acc, p) => {
            const id = String(p?.project_id ?? p?.id ?? "");
            if (!id) return acc;
            const name = String(p?.project_name ?? p?.name ?? id);
            acc[id] = name;
            return acc;
          }, {});
          if (Object.keys(mapped).length > 0) {
            dispatch(mergeProjectNamesById(mapped));
          }
        }
      } catch (e) {
        /* ignore */
      }
    } catch (error) {
      showError(error);
      dispatch(setOrganizationError(error));
    } finally {
      if (reset) {
        dispatch(setOrganizationLoading(false));
      } else {
        dispatch(setOrganizationLoadingMore(false));
      }
    }
  };

  const fetchMoreProjects = async (org_id: string) => {
    const {
      loadingMore,
      hasMore,
      searchTerm = "",
    } = store.getState().organization;

    if (!loadingMore && hasMore) {
      await fetchAllProjects(org_id, false, undefined, searchTerm);
    }
  };

  /**
   * Fetch combined roadmaps and projects from the new unified API
   * GET /organizations/roadmaps_and_projects
   *
   * Uses a request ID counter to prevent stale responses from corrupting state.
   * If a newer request is made before this one completes, the stale response is discarded.
   */
  const requestIdRef = useRef(0);

  const fetchRoadmapsAndProjects = async (
    reset: boolean = true,
    pageNumber?: number,
    searchTerm: string = "",
    searchType: string = "all", // 'all', 'roadmap', 'project'
    sortBy: string = "modified_at_desc",
    source: string = "",
    quarter: string[] = [],
    pinned: boolean = false,
    priorityFilter: string[] = [],
  ) => {
    const requestId = ++requestIdRef.current;

    if (reset) {
      if (pageNumber) {
        dispatch(setWorkItemsLoading(true));
      } else {
        dispatch(clearWorkItemsData());
        dispatch(setWorkItemsLoading(true));
      }
    } else {
      dispatch(setWorkItemsLoadingMore(true));
    }

    try {
      const { workItemsPerPage = 10 } = store.getState().organization;
      const currentPage = reset
        ? pageNumber || 1
        : store.getState().organization.workItemsPage + 1;

      // Build query parameters
      const params = new URLSearchParams({
        page: String(currentPage),
        per_page: String(workItemsPerPage),
        search: searchTerm,
        sort_by: sortBy,
        pinned: String(pinned),
        ...(searchType === "all" ? {} : { item_type: searchType }),
      });
      if (source) {
        params.append("source", source);
      }
      // Send quarter filter if provided
      if (quarter.length > 0) {
        params.append("quarter", quarter.join(","));
      }
      if (priorityFilter.length > 0) {
        params.append("priority", priorityFilter.join(","));
      }

      const response = await axios.get<any>(
        `/organizations/roadmaps_and_projects?${params.toString()}`,
      );

      // Discard stale response if a newer request was made
      if (requestId !== requestIdRef.current) return;

      const responseData = response.data;
      if (reset) {
        dispatch(setWorkItemsData(responseData));
        dispatch(
          setWorkItemsFilters({
            searchType: searchType as "all" | "roadmap" | "project" | "epic" | "feature",
            sortBy,
            sourceFilter: source,
            quarterFilter: quarter,
            pinnedFilter: pinned,
            priorityFilter,
          }),
        );
      } else {
        dispatch(appendWorkItemsData(responseData));
      }

      // Populate global ID->name cache
      try {
        const items: any[] = responseData.items || [];
        if (items.length > 0) {
          const buildNameMap = (type: string) =>
            items
              .filter((p: any) => p.type === type)
              .reduce<Record<string, string>>((acc, p) => {
                const id = String(p?.id ?? "");
                if (id) acc[id] = String(p?.name ?? id);
                return acc;
              }, {});

          const projectMapped = buildNameMap("project");
          if (Object.keys(projectMapped).length > 0) {
            dispatch(mergeProjectNamesById(projectMapped));
          }
          const roadmapMapped = buildNameMap("roadmap");
          if (Object.keys(roadmapMapped).length > 0) {
            dispatch(mergeRoadmapNamesById(roadmapMapped));
          }
        }
      } catch (_) {
        /* ignore cache errors */
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        showError(error);
        dispatch(setOrganizationError(error));
      }
    } finally {
      if (requestId === requestIdRef.current) {
        if (reset) {
          dispatch(setWorkItemsLoading(false));
        } else {
          dispatch(setWorkItemsLoadingMore(false));
        }
      }
    }
  };
  

  /**
   * Fetch more items for infinite scroll using the combined API
   * Uses stored filter state from redux for consistent pagination
   */
  const fetchMoreRoadmapsAndProjects = async () => {
    const {
      workItemsLoadingMore,
      workItemsHasMore,
      workItemsSearchTerm = "",
      workItemsSearchType = "all",
      workItemsSortBy = "modified_at_desc",
      workItemsSourceFilter = "",
      workItemsQuarterFilter = [],
      workItemsPinnedFilter = false,
      workItemsPriorityFilter = [],
    } = store.getState().organization;

    if (!workItemsLoadingMore && workItemsHasMore) {
      await fetchRoadmapsAndProjects(
        false,
        undefined,
        workItemsSearchTerm,
        workItemsSearchType,
        workItemsSortBy,
        workItemsSourceFilter,
        workItemsQuarterFilter,
        workItemsPinnedFilter,
        workItemsPriorityFilter,
      );
    }
  };

  /**
   * Fetch recent roadmaps and projects combined
   * Returns the most recently modified items sorted by modification date (newest first)
   * @param limit Number of items per type (1-10), returns up to limit roadmaps + limit projects
   */
  const fetchPinnedRoadmapsAndProjects = async (limit: number = 5) => {
    dispatch(setRecentProjectsLoading(true));
    try {
      const response = await axios.get<CombinedItem[]>(
        `/organizations/recent_roadmaps_and_projects?limit=${Math.min(Math.max(limit, 1), 10)}`,
      );

      // Use API response directly without transformation
      // Ensure we wrap it in an object if the reducer expects { projects: [...] }
      dispatch(setPinnedProjectsData({ projects: response.data || [] }));
      return response.data || [];
    } catch (error) {
      showError(error);
      dispatch(setRecentProjectsError(error));
      return [];
    } finally {
      dispatch(setRecentProjectsLoading(false));
    }
  };
  

  const fetchRecentProjects = async (org_id: string) => {
    dispatch(setRecentProjectsLoading(true));
    try {
      const response = await axios.get<Organization[]>(
        `/organization/${org_id}/recentprojects`,
      );
      dispatch(setPinnedProjectsData(response.data));
    } catch (error) {
      showError(error);
      dispatch(setRecentProjectsError(error));
    } finally {
      dispatch(setRecentProjectsLoading(false));
    }
  };

  const fetchTeams = async (org_id: string) => {
    dispatch(setTeamsLoading(true));
    try {
      const response = await axios.get<any>(`/organization/${org_id}/teams`);

      dispatch(setTeams(response.data.teams || response.data));
    } catch (error) {
      showError(error);
      dispatch(
        setTeamsError(
          (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "Failed to fetch teams",
        ),
      );
    } finally {
      dispatch(setTeamsLoading(false));
    }
  };

  const resetTeamsFetchedFlag = () => {
    dispatch(resetTeamsFetched());
  };

  const createTeam = async (org_id: string, teamData: any) => {
    dispatch(setTeamsLoading(true));
    try {
      const response = await axios.post(
        `/organization/${org_id}/create_team`,
        teamData,
      );
      toast.success(response.data.message || "Team created successfully");

      // Reset teams fetched flag to trigger refresh
      resetTeamsFetchedFlag();

      return response.data;
    } catch (error) {
      showError(error);
      dispatch(
        setTeamsError(
          (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "Failed to create team",
        ),
      );
      throw error;
    } finally {
      dispatch(setTeamsLoading(false));
    }
  };

  const deleteTeam = async (org_id: string, team_id: string) => {
    dispatch(setTeamsLoading(true));
    try {
      const response = await axios.delete(
        `/organization/${org_id}/delete_team/${team_id}`,
      );
      toast.success(response.data.message || "Team deleted successfully");

      // Reset teams fetched flag to trigger refresh
      resetTeamsFetchedFlag();

      return response.data;
    } catch (error) {
      showError(error);
      dispatch(
        setTeamsError(
          (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "Failed to delete team",
        ),
      );
      throw error;
    } finally {
      dispatch(setTeamsLoading(false));
    }
  };

  const updateTeam = async (org_id: string, teamData: any) => {
    dispatch(setTeamsLoading(true));
    try {
      const response = await axios.put(
        `/organization/${org_id}/create_team`,
        teamData,
      );
      toast.success(response.data.message || "Team updated successfully");

      // Reset teams fetched flag to trigger refresh
      resetTeamsFetchedFlag();

      return response.data;
    } catch (error) {
      showError(error);
      dispatch(
        setTeamsError(
          (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "Failed to update team",
        ),
      );
      throw error;
    } finally {
      dispatch(setTeamsLoading(false));
    }
  };

  // Add function to activate a team
  const activateTeam = async (org_id: string, team_id: string) => {
    dispatch(setTeamsLoading(true));
    try {
      const response = await axios.put(
        `/organization/${org_id}/reactivate_team/${team_id}`,
      );
      toast.success(response.data.message || "Team activated successfully");

      // Reset teams fetched flag to trigger refresh
      resetTeamsFetchedFlag();

      return response.data;
    } catch (error) {
      showError(error);
      dispatch(
        setTeamsError(
          (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "Failed to activate team",
        ),
      );
      throw error;
    } finally {
      dispatch(setTeamsLoading(false));
    }
  };

  // Add function to deactivate a team
  const deactivateTeam = async (org_id: string, team_id: string) => {
    dispatch(setTeamsLoading(true));
    try {
      const response = await axios.put(
        `/organization/${org_id}/deactivate_team/${team_id}`,
      );
      toast.success(response.data.message || "Team deactivated successfully");

      // Reset teams fetched flag to trigger refresh
      resetTeamsFetchedFlag();

      return response.data;
    } catch (error) {
      showError(error);
      dispatch(
        setTeamsError(
          (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "Failed to deactivate team",
        ),
      );
      throw error;
    } finally {
      dispatch(setTeamsLoading(false));
    }
  };

  const createOrg = async (itemData: Organization) => {
    dispatch(setOrganizationLoading(true));
    try {
      const response = await axios.post<Organization>(
        `/organization`,
        itemData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(
        response.data.message || "Organization created successfully.",
      );
      dispatch(setCreateOrganizationData(response.data));
      dispatch(setOrganizationUpdateSuccess(true));
    } catch (error) {
      showError(error);
      dispatch(setOrganizationError(error));
    } finally {
      dispatch(setOrganizationLoading(false));
    }
  };

  const updateOrganization = async (
    orgId: number,
    orgPayload: any,
    isfetchOrgData = false,
    errorHandling = true
  ) => {
    if (orgUpdateLoading) return;

    dispatch(setOrganizationUpdateSuccess(false));
    dispatch(setOrganizationUpdateLoading(true));

    let itemData = structuredClone(orgPayload);

    try {

      if (itemData?.onboard_settings.current_navigation) itemData.onboard_settings.current_navigation = null;
      if (itemData?.onboard_settings.current_location) itemData.onboard_settings.current_location = null;

        const response = await axios.put<UpdateOrganization>(
          `/organization/${orgId}`,
          itemData,
        );
      if (isfetchOrgData) {
        await fetchOrganization(orgId);
      } else {
        toast.success(
          response.data.message || "Organization updated successfully.",
        );
      }
      dispatch(setOrganizationUpdateSuccess(true));
    } catch (error) {
      if(errorHandling){
        showError(error);
        dispatch(setOrganizationUpdateError(error));
      }else{
        dispatch(setOrganizationUpdateLoading(false));
        throw error;
      }
    } finally {
      dispatch(setOrganizationUpdateLoading(false));
    }
  };

  const deleteItem = async (itemId: number) => {
    dispatch(setOrganizationLoading(true));
    try {
      await axios.delete<void>(`/${itemId}`);
      const updatedData = data.filter((item: any) => item.org_id !== itemId);
      dispatch(setOrganizationData(updatedData));
    } catch (error) {
      showError(error);
      dispatch(setOrganizationError(error));
    } finally {
      dispatch(setOrganizationLoading(false));
    }
  };

  const fetchOrganizationSkill = async (org_id: string) => {
    dispatch(setOrganizationSkillLoading(true));
    try {
      const response = await axios.get<Organization[]>(
        `/organization/${org_id}/skills`,
      );
      dispatch(setOrganizationSkillData(response.data));
    } catch (error) {
      showError(error);
      dispatch(setOrganizationSkillError(error));
    } finally {
      dispatch(setOrganizationSkillLoading(false));
    }
  };

  const deleteOrganizationSkill = async (org_id: string, skill_id: string) => {
    dispatch(setOrganizationSkillLoading(true));
    try {
      const response = await axios.delete<any>(
        `/organization/${org_id}/skill/${skill_id}`,
      );
      toast.success(response.data.message || "Skill deleted successfully.");
      fetchOrganizationSkill(org_id);
      dispatch(setShowSkillEdit(false));
    } catch (error) {
      showError(error);
      dispatch(setOrganizationSkillError(error));
    } finally {
      dispatch(setOrganizationSkillLoading(false));
      dispatch(setShowSkillEdit(false));
    }
  };

  const addSkills = async (org_id: string, itemData: any) => {
    dispatch(setOrganizationSkillLoading(true));
    try {
      const response = await axios.post<Organization>(
        `/organization/${org_id}/skill`,
        itemData,
      );
      toast.success(response.data.message || "Skills added successfully.");
      fetchOrganizationSkill(org_id);
      dispatch(setShowSkillEdit(false));
    } catch (error) {
      showError(error);
      dispatch(setOrganizationSkillError(error));
    } finally {
      dispatch(setOrganizationSkillLoading(false));
    }
  };
  const inviteUsers = async (org_id: string, itemData: any) => {
    dispatch(setInviteUsersSuccess(false));
    dispatch(setInviteUsersLoading(true));
    try {
      const response = await axios.post<Organization>(
        `/organization/${org_id}/invite-user`,
        itemData,
      );
      toast.success(response.data.message || "Users invited successfully.");
      dispatch(setInviteUsersSuccess(true));
    } catch (error) {
      showError(error);
      dispatch(setInviteUsersError(error));
    } finally {
      dispatch(setInviteUsersLoading(false));
    }
  };

  const updateSkills = async (org_id: string, itemData: any) => {
    dispatch(setOrganizationUpdateSuccess(false));
    dispatch(setOrganizationLoading(true));
    try {
      const response = await axios.put<UpdateOrganization>(
        `/organization/${org_id}/skill/${itemData.id}`,
        itemData,
      );
      toast.success(response.data.message || "Skills updated successfully.");
      fetchOrganizationSkill(org_id);
      dispatch(setShowSkillEdit(false));
    } catch (error) {
      showError(error);
      dispatch(setOrganizationUpdateError(error));
    } finally {
      dispatch(setOrganizationLoading(false));
    }
  };

  const getJiraSettings = async () => {
    dispatch(setJiraSettingsGetSuccess(false));
    dispatch(setJiraSettingsLoading(true));
    try {
      const response = await axios.get<UpdateOrganization>(
        `/integration/jira_settings`,
      );
      dispatch(setJiraSettings(response.data));
      return response;
    } catch (error) {
      showError(error);
      dispatch(setJiraSettingsGetError(error));
    } finally {
      dispatch(setJiraSettingsLoading(false));
    }
  };

  const saveJiraSettings = async (org_id: string, itemData: any) => {
    dispatch(setJiraSettingsSaveSuccess(false));
    dispatch(setJiraSettingsLoading(true));
    try {
      const response = await axios.post<any>(
        `/integration/jira_settings`,
        itemData,
      );
      toast.success(
        response.data.message || "Jira settings saved successfully.",
      );
      await getJiraSettings();
      await fetchOrgIntegrations();
    } catch (error) {
      showError(error);
      dispatch(setJiraSettingsSaveError(error));
      throw error;
    } finally {
      dispatch(setJiraSettingsLoading(false));
    }
  };

  const getAsanaSettings = async (org_id: string, itemData?: any) => {
    dispatch(setAsanaSettingsGetSuccess(false));
    dispatch(setAsanaSettingsLoading(true));
    try {
      const response = await axios.get<UpdateOrganization>(
        `/organization/${org_id}/asana_integration_settings`,
        itemData,
      );
      dispatch(setAsanaSettings(response.data));
    } catch (error) {
      showError(error);
      dispatch(setAsanaSettingsGetError(error));
    } finally {
      dispatch(setAsanaSettingsLoading(false));
    }
  };

  const saveAsanaSettings = async (org_id: string, itemData: any) => {
    dispatch(setAsanaSettingsSaveSuccess(false));
    dispatch(setAsanaSettingsLoading(true));
    try {
      const response = await axios.post<any>(
        `/organization/${org_id}/asana_integration_settings`,
        itemData,
      );
      toast.success(
        response.data.message || "Asana settings saved successfully.",
      );
      await fetchOrgIntegrations();
    } catch (error) {
      showError(error);
      dispatch(setAsanaSettingsSaveError(error));
    } finally {
      dispatch(setAsanaSettingsLoading(false));
    }
  };

  const saveProjectReference = async (org_id: string, itemData: any) => {
    dispatch(setProjectReferenceLoading(true));
    try {
      const response = await axios.post<any>(
        `/organization/${org_id}/project_reference`,
        itemData,
      );
      toast.success(
        response.data.message || "Project reference saved successfully.",
      );
    } catch (error) {
      showError(error);
      dispatch(setProjectReferenceSaveError(error));
    } finally {
      dispatch(setProjectReferenceLoading(false));
    }
  };

  const getProjectReference = async (org_id: string, itemData: any) => {
    dispatch(setProjectReferenceLoading(true));
    try {
      const response = await axios.get<UpdateOrganization>(
        `/organization/${org_id}/project_reference`,
        itemData,
      );
      dispatch(projectReferenceRes(response.data));
    } catch (error) {
      showError(error);
      dispatch(setProjectReferenceSaveError(error));
    } finally {
      dispatch(setProjectReferenceLoading(false));
    }
  };

  const getOrgPreferences = async (org_id: string) => {
    dispatch(setOrgPreferencesLoading(true));
    try {
      const response = await axios.get<{ preferences: Preference[] }>(
        `/organization/${org_id}/preferences`,
      );
      dispatch(orgPreferencesRes(response?.data?.preferences));
    } catch (error) {
      showError(error);
      dispatch(setOrgPreferencesError(error));
    } finally {
      dispatch(setOrgPreferencesLoading(false));
    }
  };

  const deleteAllOrgPreferences = async (org_id: string) => {
    dispatch(setOrgPreferencesLoading(true));
    try {
      const response = await axios.delete<any>(
        `/organization/${org_id}/preferences`,
      );
      toast.success(
        response.data.message || "All preferences deleted successfully.",
      );
      await getOrgPreferences(org_id);
    } catch (error) {
      showError(error);
      dispatch(setOrgPreferencesError(error));
    } finally {
      dispatch(setOrgPreferencesLoading(false));
    }
  };

  const deleteSingleOrgPreferences = async (org_id: string, pref_id: any) => {
    dispatch(setOrgPreferencesLoading(true));
    try {
      await axios.delete<any>(`/organization/${org_id}/preferences/${pref_id}`);
      await getOrgPreferences(org_id);
    } catch (error) {
      showError(error);
      dispatch(setOrgPreferencesError(error));
    } finally {
      dispatch(setOrgPreferencesLoading(false));
    }
  };

  const saveOrgPreferences = async (org_id: string, itemData: any) => {
    dispatch(setOrgPreferencesLoading(true));
    try {
      const response = await axios.post<any>(
        `/organization/${org_id}/preference`,
        itemData,
      );
      dispatch(
        updatePreferenceId({
          oldId: -1,
          newId: response?.data?.pref_id,
        }),
      );
      await getOrgPreferences(org_id);
    } catch (error) {
      showError(error);
      dispatch(setOrgPreferencesError(error));
    } finally {
      dispatch(setOrgPreferencesLoading(false));
    }
  };

  const fetchOrgWorkingDays = async (org_id: any) => {
    dispatch(setOrgWorkingDaysLoading(true));
    try {
      const response: any = await axios.get<any[]>(
        `/organization/${org_id}/working_days`,
      );
      dispatch(setOrgWorkingDays(response?.data?.working_days));
    } catch (error) {
      showError(error);
    } finally {
      dispatch(setOrgWorkingDaysLoading(false));
    }
  };

  const saveAzureSettings = async (org_id: string, itemData: any) => {
    dispatch(setAzureSettingsSaveSuccess(false));
    dispatch(setAzureSettingsLoading(true));
    try {
      const response = await axios.post<any>(
        `/integration/azure_settings`,
        itemData,
      );
      toast.success(
        response.data.message || "Azure settings saved successfully.",
      );
      await getAzureSettings(org_id);
      await fetchOrgIntegrations();
    } catch (error) {
      showError(error);
      dispatch(setAzureSettingsSaveError(error));
      throw error;
    } finally {
      dispatch(setAzureSettingsLoading(false));
    }
  };
  const getAzureSettings = async (org_id: string, itemData?: any) => {
    dispatch(setAzureSettingsGetSuccess(false));
    dispatch(setAzureSettingsLoading(true));
    try {
      const response = await axios.get<UpdateOrganization>(
        `/integration/azure_settings`,
        itemData,
      );
      dispatch(setAzureSettings(response.data));
      return response;
    } catch (error) {
      showError(error);
      dispatch(setAzureSettingsGetError(error));
      throw error;
    } finally {
      dispatch(setAzureSettingsLoading(false));
    }
  };

  const saveMondaySettings = async (org_id: string, itemData: any) => {
    dispatch(setMondaySettingsSaveSuccess(false));
    dispatch(setMondaySettingsLoading(true));
    try {
      const response = await axios.post<any>(
        `/integration/monday_settings`,
        itemData,
      );
      toast.success(
        response.data.message || "Monday settings saved successfully.",
      );
      await getMondaySettings(org_id);
      await fetchOrgIntegrations();
    } catch (error) {
      showError(error);
      dispatch(setMondaySettingsSaveError(error));
    } finally {
      dispatch(setMondaySettingsLoading(false));
    }
  };
  const getMondaySettings = async (org_id: string, itemData?: any) => {
    dispatch(setMondaySettingsGetSuccess(false));
    dispatch(setMondaySettingsLoading(true));
    try {
      const response = await axios.get<UpdateOrganization>(
        `/integration/monday_settings`,
        itemData,
      );
      dispatch(setMondaySettings(response.data));
    } catch (error) {
      showError(error);
      dispatch(setMondaySettingsGetError(error));
    } finally {
      dispatch(setMondaySettingsLoading(false));
    }
  };

  const getAllTolerance = async () => {
    dispatch(setToleranceModesLoading(true));
    try {
      const response = await axios.get<{ modes: any[] }>(
        `/organization/tolerance_modes`,
      );
      dispatch(setToleranceModes(response.data.modes || []));
    } catch (error) {
      showError(error);
      dispatch(setToleranceModesError(error));
    } finally {
      dispatch(setToleranceModesLoading(false));
    }
  };

  const fetchOrgSchedule = async (org_id: string) => {
    if (!org_id) return;
    dispatch(setOrgScheduleLoading(true));
    try {
      const response = await axios.get<Organization[]>(
        `/organization/${org_id}/holidays`,
      );
      dispatch(setOrgScheduleData(response.data));
    } catch (error) {
      showError(error);
      dispatch(setOrgScheduleError(error));
    } finally {
      dispatch(setOrgScheduleLoading(false));
    }
  };

  const deleteOrgSchedule = async (org_id: string, payload: any) => {
    dispatch(setOrgScheduleLoading(true));
    try {
      const response = await axios.delete<any>(
        `/organization/${org_id}/holiday/${payload.id}`,
      );
      toast.success(response.data.message || "Schedule deleted successfully.");
      fetchOrgSchedule(org_id);
      dispatch(setshoworgScheduleEdit(false));
    } catch (error) {
      showError(error);
      dispatch(setOrgScheduleError(error));
    } finally {
      dispatch(setOrgScheduleLoading(false));
      dispatch(setshoworgScheduleEdit(false));
    }
  };

  const addOrgSchedule = async (org_id: string, itemData: any) => {
    dispatch(setOrgScheduleLoading(true));
    try {
      const response = await axios.post<Organization>(
        `/organization/${org_id}/holiday`,
        itemData,
      );
      toast.success(response.data.message || "Schedule added successfully.");
      fetchOrgSchedule(org_id);
      dispatch(setshoworgScheduleEdit(false));
    } catch (error) {
      showError(error);
      dispatch(setOrgScheduleError(error));
    } finally {
      dispatch(setOrgScheduleLoading(false));
    }
  };

  const updateOrgSchedule = async (org_id: string, itemData: any) => {
    dispatch(setOrgScheduleLoading(true));
    try {
      const response = await axios.put<Organization>(
        `/organization/${org_id}/holiday`,
        itemData,
      );
      toast.success(response.data.message || "Schedule updated successfully.");
      fetchOrgSchedule(org_id);
      dispatch(setshoworgScheduleEdit(false));
    } catch (error) {
      showError(error);
      dispatch(setOrgScheduleError(error));
    } finally {
      dispatch(setOrgScheduleLoading(false));
    }
  };

  const fetchOrgIntegrations = async () => {
    if (activeIntegrationsLoading) return;

    dispatch(setActiveIntegrationsLoading(true));
    try {
      const response = await axios.get("/integration/active");
      if (response?.data?.integrations) {
        dispatch(setActiveIntegrations(response.data.integrations));
        return response.data.integrations;
      }
      return [];
    } catch (error) {
      // showError(error);
      return [];
    } finally {
      dispatch(setActiveIntegrationsLoading(false));
    }
  };

  const fetchPendingInvites = async (org_id: string) => {
    dispatch(setPendingInvitesLoading(true));
    try {
      const response = await axios.get(
        `/organization/${org_id}/pending_resources`,
      );
      dispatch(setPendingInvites(response.data.resources || []));
    } catch (error) {
      showError(error);
      dispatch(setPendingInvitesError(error));
    } finally {
      dispatch(setPendingInvitesLoading(false));
    }
  };

  const revokeInvite = async (org_id: string, invitation_id: string) => {
    dispatch(setRevokeInviteLoading(true));
    dispatch(setRevokeInviteSuccess(false));
    try {
      const response = await axios.post(
        `/organization/${org_id}/revoke-invite`,
        {
          invitation_id: invitation_id,
        },
      );
      toast.success(response.data.message || "Invite revoked successfully");
      dispatch(setRevokeInviteSuccess(true));
      await fetchPendingInvites(org_id);
    } catch (error) {
      showError(error);
      dispatch(setRevokeInviteError(error));
    } finally {
      dispatch(setRevokeInviteLoading(false));
    }
  };

  const addGithubIntegration = async (itemData: any) => {
    dispatch(setGithubSettingsLoading(true));
    try {
      const response = await axios.post<any>(
        "/integration/github_settings",
        itemData,
      );
      toast.success(
        response.data.message || "GitHub integration added successfully.",
      );
      await getGithubSettings();
      await fetchOrgIntegrations();
      return response;
    } catch (error) {
      showError(error);
      dispatch(
        setGithubSettingsError(
          (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "Failed to add GitHub integration",
        ),
      );
      throw error;
    } finally {
      dispatch(setGithubSettingsLoading(false));
    }
  };

  const getGithubSettings = async () => {
    dispatch(setGithubSettingsLoading(true));
    try {
      const response = await axios.get<any>("/integration/github_settings");
      dispatch(setGithubSettings(response.data));
      return response;
    } catch (error) {
      showError(error);
      dispatch(
        setGithubSettingsError(
          (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "Failed to get GitHub settings",
        ),
      );
      throw error;
    } finally {
      dispatch(setGithubSettingsLoading(false));
    }
  };

  const updateGithubIntegration = async (itemData: any) => {
    dispatch(setGithubSettingsLoading(true));
    try {
      const response = await axios.put<any>(
        "/integration/github_settings",
        itemData,
      );
      toast.success(
        response.data.message || "GitHub integration updated successfully.",
      );
      await getGithubSettings();
      await fetchOrgIntegrations();
      return response;
    } catch (error) {
      showError(error);
      dispatch(
        setGithubSettingsError(
          (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "Failed to update GitHub integration",
        ),
      );
      throw error;
    } finally {
      dispatch(setGithubSettingsLoading(false));
    }
  };

  const fetchSprints = async (
    type: string = "azure",
    status?: string,
  ): Promise<Sprint[]> => {
    try {
      const params = new URLSearchParams();
      params.append("type", type);
      if (status) {
        params.append("status", status);
      }

      const response = await axios.get<Sprint[]>(
        `/integration/sprints?${params.toString()}`,
      );
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const fetchProjectNamesById = async (orgId: string, projectIds: string[]) => {
    try {
      const response = await axios.post<ProjectNamesResponse>(
        `/organization/${orgId}/projects/by_ids`,
        {
          ids: projectIds,
        },
      );

      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const fetchRoadmapNamesById = async (roadmapIds: string[]) => {
    try {
      const response = await axios.post<RoadmapNamesResponse>(
        `/roadmaps/by_ids`,
        {
          ids: roadmapIds,
        },
      );

      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  // Convenience helpers: fetch and upsert name maps into Redux
  const upsertProjectNamesByIds = async (
    orgId: string,
    projectIds: string[],
  ) => {
    try {
      const res = await fetchProjectNamesById(orgId, projectIds);
      const mapped = (res?.projects || []).reduce<Record<string, string>>(
        (acc, item) => {
          if (item?.id) acc[item.id] = item?.name || item.id;
          return acc;
        },
        {},
      );
      if (Object.keys(mapped).length > 0) {
        dispatch(mergeProjectNamesById(mapped));
      }
      return mapped;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const upsertRoadmapNamesByIds = async (roadmapIds: string[]) => {
    try {
      const res = await fetchRoadmapNamesById(roadmapIds);
      const mapped = (res?.roadmaps || []).reduce<Record<string, string>>(
        (acc, item) => {
          if (item?.id) acc[item.id] = item?.name || item.id;
          return acc;
        },
        {},
      );
      if (Object.keys(mapped).length > 0) {
        dispatch(mergeRoadmapNamesById(mapped));
      }
      return mapped;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  /**
   * Sync all roadmaps in a project by name
   * @param projectName The name of the project (Azure project name or Jira project)
   * @param integrationType The type of integration ('azure' or 'jira')
   * @param projectId The project ID from the integration
   * @returns Promise with the sync result message
   */
  const syncIntegrationProject = async (
    projectName: string,
    integrationType: string,
    projectId: string,
  ): Promise<{ message: string }> => {
    try {
      const response = await axios.post<{ message: string }>(
        "/integration/project/sync",
        {
          integration_project_name: projectName,
          integration_type: integrationType,
          integration_project_id: projectId,
        },
      );
      toast.success(response.data.message || "Sync initiated successfully");
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const deleteOrgCache = async () => {
    try {
      const response = await axios.delete<any>('/organization/delete-cache');
      toast.success("System refreshed successfully. New Changes will reflect in your work items shortly.");
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const refreshJiraPriorities = async () => {
    try {
      const response = await axios.post('/integration/jira/refresh_priorities');
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const refreshAzurePriorities = async () => {
    try {
      const response = await axios.post('/integration/azure/refresh_priorities');
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const updateJiraPriorities = async (payload: any) => {
    try {
      const response = await axios.post("/integration/jira/update_priorities", {
        priorities:payload,
      });
      toast.success(response.data.message || "Jira priorities updated successfully");
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const updateAzurePriorities = async (payload: any) => {
    try {
      const response = await axios.post(
        "/integration/azure/update_priorities",
        {
          priorities: payload,
        },
      );
      toast.success(response.data.message || "Azure priorities updated successfully");
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const pinWorkItem = async (payload: {
    id: string;
    type: string;
    pin: boolean;
  }) => {
    try {

      const payloadFinal = [{
        id: payload.id,
        type: payload.type == "feature" ? "project":payload.type,
        pin: payload.pin,
      }]
      const response = await axios.post('/organizations/pin', payloadFinal);
      toast.success(
        response.data.message ||
          (payload.pin
            ? "Item pinned successfully"
            : "Item unpinned successfully"),
      );
      fetchPinnedItems();
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };
  const pinWorkItems = async (payload: {
    id: string;
    type: string;
    pin: boolean;
  }[]) => {
    try {

      const response = await axios.post('/organizations/pin', payload);
      toast.success(
        response.data.message || "Items pinned successfully"
            
      );
      fetchPinnedItems();
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const fetchPinnedItems = async (props?: {dependentOnRedux: boolean}) => {
    let {dependentOnRedux= true} = props ?? {};
    
    if(dependentOnRedux) dispatch(setRecentProjectsLoading(true));
    
    try {
      const response = await axios.get<CombinedItem[]>(`/organizations/pinned`);

      // Use API response directly without transformation
      // Ensure we wrap it in an object if the reducer expects { projects: [...] }
      if(dependentOnRedux) dispatch(setPinnedProjectsData({ projects: response.data || [] }));
      return response.data || [];
    } catch (error) {
      showError(error);
      if(dependentOnRedux) dispatch(setRecentProjectsError(error));
      return [];
    } finally {
      if(dependentOnRedux) dispatch(setRecentProjectsLoading(false));
    }
  };

    return {
      pinWorkItem,
      pinWorkItems,
      fetchPinnedItems,
      fetchOrganization,
      fetchAllProjects,
      createOrg,
      updateOrganization,
      deleteItem,
      fetchOrganizationSkill,
      addSkills,
      updateSkills,
      fetchRecentProjects,
      inviteUsers,
      saveJiraSettings,
      getJiraSettings,
      saveProjectReference,
      getProjectReference,
      getAsanaSettings,
      saveAsanaSettings,
      deleteOrganizationSkill,
      getOrgPreferences,
      deleteAllOrgPreferences,
      saveOrgPreferences,
      deleteSingleOrgPreferences,
      fetchOrgWorkingDays,
      saveAzureSettings,
      getAzureSettings,
      saveMondaySettings,
      getMondaySettings,
      getAllTolerance,
      deleteOrgSchedule,
      updateOrgSchedule,
      addOrgSchedule,
      fetchOrgSchedule,
      fetchMoreProjects,
      fetchRoadmapsAndProjects,
      fetchMoreRoadmapsAndProjects,
      fetchPinnedRoadmapsAndProjects,
      fetchOrgIntegrations,
      fetchTeams,
      resetTeamsFetchedFlag,
      createTeam,
      deleteTeam,
      updateTeam,
      fetchPendingInvites,
      revokeInvite,
      activateTeam,
      deactivateTeam,
      fetchSprints,
      fetchProjectNamesById,
      fetchRoadmapNamesById,
      upsertProjectNamesByIds,
      upsertRoadmapNamesByIds,
      addGithubIntegration,
      getGithubSettings,
      updateGithubIntegration,
      syncIntegrationProject,
      fetchProjectsList,
      deleteOrgCache,
      refreshJiraPriorities,
      refreshAzurePriorities,
      updateJiraPriorities,
      updateAzurePriorities,
    };
};

export default useOrganizationService;
